import { Coord, Row, colIndex, indexToCol, COLS, ROWS, coordKey } from '../types/grid.js'
import { Ship } from '../types/ship.js'
import { hasTrait } from '../types/ship.js'
import { getShipCells } from './placement.js'

/**
 * Compute the clipped 3×3 sonar zone centered on a coordinate.
 * GDD §4.3: If the center is near an edge, the zone is clipped to the grid boundary.
 * e.g. center A1 → zone is A1:B2 (2×2)
 */
export function getSonarZone(center: Coord): Coord[] {
  const ci = colIndex(center.col)
  const ri = center.row

  const minCol = Math.max(0, ci - 1)
  const maxCol = Math.min(COLS.length - 1, ci + 1)
  const minRow = Math.max(1, ri - 1)
  const maxRow = Math.min(ROWS.length, ri + 1)

  const zone: Coord[] = []
  for (let c = minCol; c <= maxCol; c++) {
    for (let r = minRow; r <= maxRow; r++) {
      zone.push({ col: indexToCol(c), row: r as Row })
    }
  }

  return zone
}

export interface SonarResult {
  zone: Coord[]
  count: number
}

/**
 * Apply a sonar ping. Counts ship segments in the clipped zone.
 * GDD §4.3: Ghost Sub (sonar_invisible trait) is NEVER counted.
 * The function filters on the trait, not the ship name.
 */
export function applySonar(center: Coord, ships: Ship[]): SonarResult {
  const zone = getSonarZone(center)
  const zoneKeys = new Set(zone.map(coordKey))

  let count = 0
  for (const ship of ships) {
    // Skip ships with the sonar_invisible trait
    if (hasTrait(ship.type, 'sonar_invisible')) {
      continue
    }

    const cells = getShipCells(ship)
    for (const cell of cells) {
      if (zoneKeys.has(coordKey(cell))) {
        count++
      }
    }
  }

  return { zone, count }
}
